import { useChannelTalk } from '@repo/channel-talk';
import { sharePage } from '@repo/common-utils';
import { IconButton, TextButton, useToast } from '@repo/ui';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import { isInviteEventProceeding } from '@/lib/constants/event.tsx';
import { INVITE_PROMOTION_PATH } from '@/lib/constants/link.ts';
import { COMMON_PATHS } from '@/lib/constants/paths.ts';
import { useExitService } from '@/lib/hooks/use-exit-service.ts';
import { useHeaderClickEvent } from '@/lib/hooks/use-header-click-event';
import { topNavigationInquiryLinkAtom } from '@/lib/stores/common.ts';
import { LayoutClickEvent, LogoLinkType, RefundLogoColors } from '@/lib/types/layout.ts';

export const HeaderBznavRefundLogo = ({
  color,
  logoLinkType,
  clickEvent
}: {
  color: RefundLogoColors;
  logoLinkType?: LogoLinkType;
  clickEvent?: LayoutClickEvent;
}) => {
  const router = useRouter();
  const sendHeaderClickEvent = useHeaderClickEvent(`${color}Logo`, clickEvent);
  const [link, setLink] = useState('');
  const logoFileName: Record<RefundLogoColors, string> = {
    primary: 'logo_bznav-refund.svg',
    white: 'logo_bznav-refund-white.svg',
    black: 'logo_bznav-refund-black.svg',
    gray: 'logo_bznav-refund-gray.svg'
  };

  useEffect(() => {
    const logoLinks = {
      home: COMMON_PATHS.환급조회목록,
      landing: COMMON_PATHS.환급랜딩,
      currentPage: router.asPath
    };

    setLink(logoLinkType ? logoLinks[logoLinkType] || '' : '');
  }, [logoLinkType, router.asPath]);

  return (
    <div
      className={link ? 'cursor-pointer' : ''}
      onClick={() => {
        if (!link) return;
        sendHeaderClickEvent();
        router.push(link, undefined, { scroll: true });
      }}
    >
      <img
        src={`${process.env.NEXT_PUBLIC_RESOURCE_CENTER_URL}/common/logo/bznav/${logoFileName[color]}`}
        className={'px-normal-small'}
        alt={'bznav refund logo'}
      />
    </div>
  );
};

export const HeaderBackButton = ({ clickEvent }: { clickEvent?: LayoutClickEvent }) => {
  const router = useRouter();
  const sendHeaderClickEvent = useHeaderClickEvent('back', clickEvent);

  const handleBackClick = () => {
    sendHeaderClickEvent();
    router.back();
  };

  return <IconButton variant="main" size="small" iconType="chevron-left" onClick={handleBackClick} aria-label={'go-back'} />;
};

export const HeaderCloseButton = ({ clickEvent }: { clickEvent?: LayoutClickEvent }) => {
  const { exitService } = useExitService();
  const sendHeaderClickEvent = useHeaderClickEvent('close', clickEvent);

  return (
    <IconButton
      variant="main"
      size="small"
      iconType="close"
      onClick={() => {
        sendHeaderClickEvent();
        exitService();
      }}
      aria-label={'close'}
    />
  );
};

export const HeaderExitTextButton = ({ clickEvent }: { clickEvent?: LayoutClickEvent }) => {
  const { exitService } = useExitService();
  const sendHeaderClickEvent = useHeaderClickEvent('exitTextButton', clickEvent);

  return (
    <TextButton
      size="small"
      variant={'secondary'}
      onClick={() => {
        sendHeaderClickEvent();
        exitService();
      }}
    >
      나가기
    </TextButton>
  );
};

export const HeaderInquiryTextButton = ({ clickEvent }: { clickEvent?: LayoutClickEvent }) => {
  const inquiryLink = useAtomValue(topNavigationInquiryLinkAtom);
  const { openChannelTalk } = useChannelTalk();
  const sendHeaderClickEvent = useHeaderClickEvent('inquiryTextButton', clickEvent);

  return (
    <TextButton
      size="small"
      variant={'secondary'}
      onClick={() => {
        sendHeaderClickEvent();
        openChannelTalk(inquiryLink);
      }}
    >
      문의하기
    </TextButton>
  );
};

export const HeaderInquiryIconButton = ({ clickEvent }: { clickEvent?: LayoutClickEvent }) => {
  const inquiryLink = useAtomValue(topNavigationInquiryLinkAtom);
  const { openChannelTalk } = useChannelTalk();
  const sendHeaderClickEvent = useHeaderClickEvent('inquiryIconButton', clickEvent);

  return (
    <IconButton
      variant="main"
      size="small"
      iconType={'headphone'}
      aria-label={'inquiry'}
      onClick={() => {
        sendHeaderClickEvent();
        openChannelTalk(inquiryLink);
      }}
    />
  );
};

export const HeaderShareTextButton = ({ clickEvent }: { clickEvent?: LayoutClickEvent }) => {
  const router = useRouter();
  const { toast } = useToast();
  const sendHeaderClickEvent = useHeaderClickEvent('shareTextButton', clickEvent);
  const isPersonalTritx = typeof window !== 'undefined' ? sessionStorage.getItem('isPersonalTritx') === 'true' : false;

  const clickShare = () => {
    const copyContent = isPersonalTritx
      ? {
          title: '양도세 환급금 무료 진단',
          text: '1인 평균 150만원 환급, 30초 만에 무료로 진단해드려요! 기본공제 누락부터 특례 적용 여부까지 확인해보세요.',
          url: `${window.location.origin}${COMMON_PATHS.양도세환급랜딩}`
        }
      : {
          title: '개인/법인사업자 환급금 무료 조회 - 비즈넵 세금 환급',
          url: 'https://ab.bznav.com/g9htxq'
        };
    sharePage(copyContent, () => {
      toast({ title: '링크를 복사했어요.', variant: 'success' });
    });
  };

  return (
    <TextButton
      size="small"
      variant={'secondary'}
      onClick={() => {
        sendHeaderClickEvent();

        if (isInviteEventProceeding && !isPersonalTritx) {
          router.push(INVITE_PROMOTION_PATH);
          return;
        }

        clickShare();
      }}
    >
      친구 초대하기
    </TextButton>
  );
};

export const HeaderMoreIconButton = ({ clickEvent }: { clickEvent?: LayoutClickEvent }) => {
  const router = useRouter();
  const sendHeaderClickEvent = useHeaderClickEvent('moreIconButton', clickEvent);

  return (
    <IconButton
      variant="main"
      size="small"
      iconType={'ellipsis-vertical'}
      aria-label={'more-menu'}
      onClick={() => {
        sendHeaderClickEvent();
        router.push(COMMON_PATHS.MORE_MENU);
      }}
    />
  );
};
